import React, { useEffect, useState } from "react";
import { api, fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Upload, Check } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { TEMPLATES } from "../lib/templates";
import { toast } from "sonner";

export default function Settings() {
  const { user, refreshUser } = useAuth();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [logoBlob, setLogoBlob] = useState(null);

  useEffect(() => {
    if (user?.company) setForm(user.company);
    if (user?.company?.logo_path) loadLogo(user.company.logo_path);
  }, [user]);

  const loadLogo = async (path) => {
    try {
      const r = await api.get(`/files/${path}`, { responseType: "blob" });
      setLogoBlob(URL.createObjectURL(r.data));
    } catch {}
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/company", form);
      await refreshUser();
      toast.success("Configuración guardada");
    } catch { toast.error("Error al guardar"); }
    finally { setSaving(false); }
  };

  const onLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/company/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo subido");
      setForm({ ...form, logo_path: r.data.logo_path });
      loadLogo(r.data.logo_path);
      await refreshUser();
    } catch (err) { toast.error("Error al subir el logo"); }
  };

  const setTemplate = async (id) => {
    setForm({ ...form, template_id: id });
    await api.put("/company", { template_id: id });
    await refreshUser();
    toast.success("Plantilla actualizada");
  };

  return (
    <div className="space-y-8 animate-fade-in" data-testid="settings-page">
      <header>
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-brand-cyan mb-2">Ajustes</div>
        <h1 className="font-display font-bold text-4xl tracking-tight">Configuración</h1>
      </header>

      <section className="rounded-lg border border-white/10 bg-[#0A1128]/40 p-6">
        <h2 className="font-display font-semibold text-xl mb-4">Datos de empresa</h2>
        <div className="grid md:grid-cols-2 gap-4">
          {[
            ["name", "Nombre / Razón social"],
            ["cif", "CIF / NIF"],
            ["phone", "Teléfono"],
            ["email", "Email de contacto"],
            ["address", "Dirección"],
            ["website", "Web"],
          ].map(([k, lbl]) => (
            <div key={k}>
              <Label className="text-xs uppercase tracking-widest font-bold text-white/70">{lbl}</Label>
              <Input data-testid={`company-${k}`} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-1.5 bg-black/50 border-white/10 text-white" />
            </div>
          ))}
          <div>
            <Label className="text-xs uppercase tracking-widest font-bold text-white/70">IVA por defecto (%)</Label>
            <Input type="number" data-testid="company-iva" value={form.iva_default ?? 21} onChange={(e) => setForm({ ...form, iva_default: parseFloat(e.target.value) })} className="mt-1.5 bg-black/50 border-white/10 text-white" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest font-bold text-white/70">IRPF por defecto (%)</Label>
            <Input type="number" data-testid="company-irpf" value={form.irpf_default ?? 0} onChange={(e) => setForm({ ...form, irpf_default: parseFloat(e.target.value) })} placeholder="0, 7, 15 o 19" className="mt-1.5 bg-black/50 border-white/10 text-white" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs uppercase tracking-widest font-bold text-white/70">Notas por defecto (condiciones, forma de pago...)</Label>
            <Textarea data-testid="company-notes" value={form.notes_default || ""} onChange={(e) => setForm({ ...form, notes_default: e.target.value })} className="mt-1.5 bg-black/50 border-white/10 text-white" rows={3} />
          </div>
        </div>
        <Button data-testid="save-company-btn" onClick={save} disabled={saving} className="mt-6 bg-brand-orange hover:bg-brand-orange-hover">{saving ? "Guardando..." : "Guardar"}</Button>
      </section>

      <section className="rounded-lg border border-white/10 bg-[#0A1128]/40 p-6">
        <h2 className="font-display font-semibold text-xl mb-4">Logo de empresa</h2>
        <div className="flex items-center gap-6">
          <div className="w-32 h-32 rounded border border-white/10 bg-black/40 flex items-center justify-center overflow-hidden">
            {logoBlob ? <img src={logoBlob} alt="logo" className="max-w-full max-h-full object-contain" /> : <span className="text-white/30 text-xs">Sin logo</span>}
          </div>
          <div>
            <Label htmlFor="logo-input" className="cursor-pointer">
              <div data-testid="logo-upload" className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-brand-cyan/10 border border-brand-cyan/30 text-brand-cyan hover:bg-brand-cyan/20 transition-colors">
                <Upload className="w-4 h-4" /> Subir logo
              </div>
              <input id="logo-input" type="file" accept="image/*" className="hidden" onChange={onLogo} />
            </Label>
            <div className="text-xs text-white/40 mt-2">PNG, JPG, WebP o SVG. Máx 5MB.</div>
          </div>
        </div>
      </section>

      <section className="rounded-lg border border-white/10 bg-[#0A1128]/40 p-6" data-testid="templates-section">
        <h2 className="font-display font-semibold text-xl mb-1">Plantilla de documentos</h2>
        <p className="text-sm text-white/50 mb-4">Elige el diseño por defecto para tus presupuestos y facturas.</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {TEMPLATES.map((t) => (
            <button key={t.id} data-testid={`tpl-${t.id}`} onClick={() => setTemplate(t.id)}
              className={`relative p-4 rounded-lg border-2 text-left transition-all ${form.template_id === t.id ? "border-brand-cyan glow-cyan" : "border-white/10 hover:border-white/30"}`}>
              <div className="aspect-[3/4] rounded bg-white mb-2 p-2 overflow-hidden">
                <div className="h-2 mb-2 rounded" style={{ background: t.accent }} />
                <div className="space-y-1">
                  {[...Array(6)].map((_, i) => <div key={i} className="h-1 bg-gray-200 rounded" style={{ width: `${80 - i * 8}%` }} />)}
                </div>
                <div className="mt-3 flex justify-end"><div className="h-2 w-12 rounded" style={{ background: t.accent }} /></div>
              </div>
              <div className="font-display font-semibold text-sm text-white">{t.name}</div>
              <div className="text-[10px] text-white/50">{t.description}</div>
              {form.template_id === t.id && <Check className="absolute top-2 right-2 w-4 h-4 text-brand-cyan" />}
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}
